import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';
import { CHORDS, MAJOR, PARTIDO, strokes, tonesOf, under, wrap } from './samba-parts';

const PROGS = [
  ['C', 'A7', 'Dm', 'G7'],
  ['Am', 'Dm', 'E7', 'Am'],
  ['C', 'C7', 'F', 'G7'],
  ['Dm', 'A7', 'Dm', 'A7'],
] as const;

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'G5', 2], [4, 'A5', 1, 0.8], [5, 'G5', 3], [8, 'E5', 2], [10, 'D5', 1, 0.8], [11, 'C5', 3], [14, 'D5', 2]],
  [[0, 'C#5', 2], [3, 'E5', 3], [6, 'G5', 2], [8, 'A5', 4], [12, 'G5', 2], [14, 'E5', 2]],
  [[0, 'F5', 2], [2, 'A5', 2], [4, 'D6', 1, 0.8], [5, 'C6', 3], [8, 'A5', 2], [10, 'F5', 1, 0.8], [11, 'A5', 3], [14, 'G5', 2]],
  [[0, 'F5', 3], [3, 'D5', 3], [6, 'B4', 2], [8, 'D5', 3], [11, 'G5', 1, 0.8], [12, 'F5', 2], [14, 'D5', 1, 0.8], [15, 'E5', 1, 0.85]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 1.6], [3, 0, 0.9], [4, 0, 3.4], [8, 7, 1.6], [11, 7, 0.9], [12, 7, 3.4]],
  [[0, 0, 2.6], [3, 7, 0.9], [4, 0, 3.4], [8, 12, 1.6], [11, 7, 0.9], [12, 0, 3.4]],
];

const KEYS = [0, 2, -3, 5, -2, 3, -1] as const;
const RUN = [5, 4, 2, 1] as const;
const JINGLE = [0.75, 0.4, 0.6, 0.9] as const;
const TANTAS = ['.X..x.X.X..x..X.', '.X..x.X.X..xX.x.'] as const;
const FILLS = ['....x.x.X.x.XxXX', '........X.xXx.XX'] as const;
const CONVENCOES = ['X..X..X.', 'X.X..X..'] as const;
const TAMBORIM = 'X.xx.x.xX.xx.x.x';
const REPIQUE = '..x..x.x..x.x..x';
const RESPOSTA = 'X.x..x.xX.x..xX.';
const SWING = 54;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(SAMBA, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const tantaP = ch.pick(TANTAS);
  const fillP = ch.pick(FILLS);
  const convP = ch.pick(CONVENCOES);
  const bassRow = ch.pick(BASSES);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: tonesOf(c) }]),
        MAJOR,
        [69, 86],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, SWING, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const rng = createRng(`samba-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const seen = new Map<string, Ev>();
  const hit = (v: string, bar: number, step: number, vel: number) => {
    const had = seen.get(`${v}:${bar}:${step}`);
    if (had !== undefined) {
      had.vel = Math.max(had.vel, vel);
      return;
    }
    const ev: Ev = { v, t: g.at(bar, step), bar, step, vel };
    seen.set(`${v}:${bar}:${step}`, ev);
    events.push(ev);
  };
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });

  const pattern = (v: string, bar: number, pat: string, scale: number, from = 0, cut = 16) => {
    for (const h of hits(pat)) if (h.step >= from && h.step < cut) hit(v, bar, h.step, human(h.vel * scale));
  };
  const pandeiro = (bar: number, scale: number, slaps: boolean, from = 0) => {
    for (let step = from; step < 16; step++) hit('pandJ', bar, step, human(JINGLE[step % 4]! * scale));
    for (const step of [0, 8]) if (step >= from) hit('pandB', bar, step, human(0.35 * scale));
    for (const step of [4, 12]) if (step >= from) hit('pandB', bar, step, human(0.8 * scale));
    if (slaps) for (const step of [6, 14]) if (step >= from) hit('pandS', bar, step, human(0.8 * scale));
  };
  const surdo = (bar: number, scale: number) => {
    for (const step of [0, 8]) hit('surdoM', bar, step, human(0.75 * scale));
    for (const step of [4, 12]) hit('surdo', bar, step, human(scale));
  };
  const tanta = (bar: number, scale: number) => {
    for (const k of strokes(tantaP, 0)) {
      if (k.open) hit('tanta', bar, k.step, human(k.vel * scale));
      else if (k.step % 2 === 0) hit('tantaM', bar, k.step, human(0.45 * scale));
    }
  };
  const cavaco = (bar: number, notes: readonly string[], scale: number) => {
    for (const k of strokes(PARTIDO, 0.3)) {
      if (k.open) chord('cavaco', bar, k.step, k.up ? [...notes].reverse() : notes, s * 1.3, human(k.vel * scale), 0.008);
      else hit('chk', bar, k.step, human(k.vel * scale));
    }
  };
  const virada = (bar: number, scale: number) => {
    for (const h of hits(fillP)) hit('tanta', bar, h.step, 0.4 + (scale * 0.6 * h.step) / 15);
    hit('surdo', bar, 12, 0.85 * scale);
    hit('surdo', bar, 14, 0.95 * scale);
  };

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const tones = tonesOf(c);
    const cav = c.cav.map(tr);
    const root = midi(wrap(tr(c.root), 34, 45));
    const next = midi(wrap(tr(prog[cycle(i + 1, 4)]!.root), 34, 45));
    const drop = b.name === 'drop' || b.name === 'drop2';
    const row = hook[i]!;
    const call = i % 2 === 0;
    const bass = (scale: number, run: boolean) => {
      for (const [step, semis, len] of bassRow) {
        if (run && step >= 8) continue;
        play('violao', b.bar, step, noteName(root + semis), len * s * 0.9, human(0.9 * scale));
      }
      if (run) RUN.forEach((d, k) => play('violao', b.bar, 8 + k * 2, noteName(next + d), s * 1.8, human(0.85 * scale)));
    };
    const melody = (v: string, vel: number, harm: number) => {
      for (const [step, note, len, nv] of row) {
        play(v, b.bar, step, tr(note), len * s * 0.92, human((nv ?? 0.95) * vel));
        if (harm > 0) play(v, b.bar, step, tr(under(note, tones)), len * s * 0.9, (nv ?? 0.9) * harm);
      }
    };

    if (end) {
      hit('surdo', b.bar, 0, 1);
      hit('tanta', b.bar, 0, 1);
      hit('pandB', b.bar, 0, 0.9);
      hit('pandJ', b.bar, 0, 0.9);
      hit('impact', b.bar, 0, 0.8);
      chord('cavaco', b.bar, 0, cav, g.barLength * 0.6, 0.95, 0.014);
      chord('coro', b.bar, 0, cav, g.barLength * 0.7, 0.8);
      play('violao', b.bar, 0, noteName(root), g.barLength * 0.6, 0.95);
      continue;
    }

    if (b.name === 'intro') {
      melody('solo', 0.75, 0);
      bass(0.55, false);
      if (b.index >= b.bars / 2) pandeiro(b.bar, 0.6, false);
      if (b.index >= 1 && !b.last) cavaco(b.bar, cav, 0.45);
      if (b.last) virada(b.bar, 0.9);
    } else if (b.name === 'A' || drop || b.name === 'outro') {
      const full = drop ? 1 : b.name === 'A' ? 0.85 : 0.8;
      pandeiro(b.bar, full, drop);
      surdo(b.bar, full);
      tanta(b.bar, drop ? 0.9 : 0.75);
      cavaco(b.bar, cav, drop ? 0.9 : call ? 0.7 : 0.85);
      bass(full, i === 3 && !b.last);
      if (drop && e >= 0.7) pattern('tamborim', b.bar, TAMBORIM, 0.8);
      if (drop && e >= 0.8) pattern('repique', b.bar, REPIQUE, 0.7);
      if (b.name === 'A') {
        if (call) melody('solo', 0.85, 0);
        else melody('lead', 0.75, 0);
        if (!call) pattern('tanta', b.bar, RESPOSTA, 0.55, 12);
      }
      if (drop) {
        if (call) melody('lead', 1, 0);
        else melody('coro', 0.95, 0.65);
        if (b.index % 8 === 0) hit('impact', b.bar, 0, b.index === 0 ? 1 : 0.6);
      }
      if (b.name === 'outro' && b.index < 2) melody(call ? 'lead' : 'coro', 0.7, call ? 0 : 0.45);
      if (b.last && (b.name === 'A' || (b.name === 'drop' && drop2 === undefined))) virada(b.bar, 1);
    } else if (b.name === 'break') {
      if (b.index === 0) {
        for (const h of hits(convP)) {
          hit('surdo', b.bar, h.step, human(h.vel));
          hit('tanta', b.bar, h.step, human(h.vel * 0.9));
          hit('pandB', b.bar, h.step, human(h.vel * 0.8));
          chord('cavaco', b.bar, h.step, cav, s * 1.2, 0.95, 0.006);
        }
        pandeiro(b.bar, 0.75, true, 8);
      } else if (b.last) {
        cavaco(b.bar, cav, 0.75);
        pandeiro(b.bar, 0.7, false);
        virada(b.bar, 1);
      } else if (b.index === 1) {
        melody('solo', 1, 0);
        pandeiro(b.bar, 0.45, false);
      } else {
        for (const h of hits(RESPOSTA)) hit('tanta', b.bar, h.step, human(h.vel * 0.85));
        pandeiro(b.bar, 0.55, true);
        bass(0.6, false);
      }
    }
  }

  for (const bar of [drop1, drop2]) if (bar !== undefined) hit('impact', bar, 0, 1);

  return {
    id: trackId(SAMBA, seed, p),
    template: SAMBA.id,
    seed,
    params: p,
    title: ch.orig ? SAMBA.ref.title : titleOf(seed),
    genre: SAMBA.genre,
    bpm: p.bpm,
    swing: SWING,
    lufs: -12,
    key,
    form,
    mix: { reverbDecay: 1.6, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.2, drive: 1.2, softness: 1.4, pumpDepth: 0.15, pumpRelease: 0.2 },
    events,
    autos: [],
    accents: { surdo: 'kick', surdoM: 'kick', tanta: 'snare', cavaco: 'stab', solo: 'melody', lead: 'melody', coro: 'melody', impact: 'drop' },
  };
}

export const SAMBA: Template = {
  id: 'samba',
  genre: 'samba / partido-alto',
  bpm: [104, 126],
  ref: { seed: 'partido-alto', title: 'Partido-alto', params: { bpm: 112, bars: 24, energy: 0.55 } },
  score,
};
